import React, { useState, useEffect } from 'react';
import { auth } from './firebase';
import { API_BASE } from './services/api';
import {
  signInWithPopup,
  signInWithRedirect,
  signInWithCustomToken,
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
  linkWithCredential,
  linkWithPopup,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';

const Auth = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // 待連結資訊：{ email, cred, originalProviderId }，撞到同 email 衝突時設定
  const [pendingLink, setPendingLink] = useState(null);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    // 檢查是否有重定向結果
    getRedirectResult(auth)
      .then((result) => {
        if (result?.user) {
          setUser(result.user);
          setError('');
        }
      })
      .catch((error) => {
        console.error('Redirect result error:', error);
        if (error.code === 'auth/account-exists-with-different-credential') {
          const email = error?.customData?.email;
          const cred =
            OAuthProvider.credentialFromError(error) ||
            GoogleAuthProvider.credentialFromError(error);
          const tried = cred?.providerId;
          const originalProviderId = tried === 'microsoft.com' ? 'google.com' : 'microsoft.com';
          setPendingLink({ email, cred, originalProviderId });
        } else {
          setError(`登入失敗: ${error.message}`);
        }
      })
      .finally(() => {
        setLoading(false);
      });

    return () => unsubscribe();
  }, []);

  // 從 OAuth 錯誤中取出待連結的憑證（Google 或 Microsoft）
  const credentialFromError = (error) =>
    OAuthProvider.credentialFromError(error) ||
    GoogleAuthProvider.credentialFromError(error);

  const providerLabel = (providerId) =>
    providerId === 'google.com' ? 'Google' : 'Microsoft';

  // 依 provider id 建立 provider 實例（可帶 login_hint 預填 email）
  const buildProvider = (providerId, email) => {
    const params = {};
    if (email) params.login_hint = email;
    if (providerId === 'google.com') {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      if (Object.keys(params).length) provider.setCustomParameters(params);
      return provider;
    }
    if (providerId && providerId.includes('microsoft')) {
      const provider = new OAuthProvider('microsoft.com');
      provider.addScope('email');
      provider.addScope('profile');
      provider.setCustomParameters({ tenant: 'common', ...params });
      return provider;
    }
    return null;
  };

  // 撞到「同 email 已用別的方式註冊」：記下待連結資訊，顯示一鍵連結按鈕
  // 本 app 只有 Google / Microsoft 兩種，原本的就是「不是這次點的」那個
  const handleAccountExists = (error, triedProviderId) => {
    const email = error?.customData?.email;
    const cred = credentialFromError(error);
    const tried = triedProviderId || cred?.providerId;
    const originalProviderId = tried === 'microsoft.com' ? 'google.com' : 'microsoft.com';
    setPendingLink({ email, cred, originalProviderId });
    setError('');
  };

  // 使用者按下「用原本方式登入並連結」：由點擊直接觸發 popup（不會被瀏覽器擋）
  const completePendingLink = async () => {
    if (!pendingLink) return;
    setError('');
    try {
      const provider = buildProvider(pendingLink.originalProviderId, pendingLink.email);
      const result = await signInWithPopup(auth, provider);
      if (pendingLink.cred) {
        await linkWithCredential(result.user, pendingLink.cred);
      }
      console.log('帳號已連結，登入成功:', result.user);
      setPendingLink(null);
    } catch (e) {
      console.error('連結失敗:', e);
      setError(`連結失敗：${e.message}`);
    }
  };

  // Google 已停用、無法登入時：用後端把 Microsoft 接管到原帳號（同一 uid），再用同一份憑證登入
  const migrateFromDeadGoogle = async () => {
    if (!pendingLink?.cred) return;
    setError('');
    setLinking(true);
    try {
      const microsoftIdToken = pendingLink.cred.idToken;
      if (!microsoftIdToken) {
        throw new Error('缺少 Microsoft 憑證，請重新點「使用 Microsoft 登入」');
      }

      const resp = await fetch(`${API_BASE}/migrate-google-to-microsoft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ microsoftIdToken }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || `接管失敗（${resp.status}）`);
      }
      const { customToken } = await resp.json();

      // 1) 用後端核發的 custom token 登入「原帳號」（落在原 uid、資料保留）
      await signInWithCustomToken(auth, customToken);

      // 2) 由 Firebase 自己把 Microsoft 連到此帳號（provider uid 由 Firebase 計算，不會錯）
      try {
        await linkWithCredential(auth.currentUser, pendingLink.cred);
      } catch (linkErr) {
        if (
          linkErr.code === 'auth/provider-already-linked' ||
          linkErr.code === 'auth/credential-already-in-use'
        ) {
          // 已經連結，無妨
        } else {
          // 憑證可能過期 → 已是原帳號身分，改用彈窗完成連結
          console.warn('linkWithCredential 失敗，改用彈窗連結:', linkErr);
          await linkWithPopup(auth.currentUser, buildProvider('microsoft.com', pendingLink.email));
        }
      }
      setPendingLink(null);
    } catch (e) {
      console.error('Microsoft 接管失敗:', e);
      setError(`接管失敗：${e.message}`);
    } finally {
      setLinking(false);
    }
  };

  // 共用登入流程：popup 失敗自動退回 redirect；同 email 衝突走一鍵連結
  const signInWithProvider = async (provider, providerId) => {
    setError('');
    setPendingLink(null);
    try {
      const result = await signInWithPopup(auth, provider);
      console.log('登入成功:', result.user);
    } catch (error) {
      console.error('彈出視窗登入失敗:', error);

      if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user') {
        try {
          await signInWithRedirect(auth, provider);
        } catch (redirectError) {
          console.error('重定向登入失敗:', redirectError);
          setError(`登入失敗: ${redirectError.message}`);
        }
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        handleAccountExists(error, providerId);
      } else {
        setError(`登入失敗: ${error.message}`);
      }
    }
  };

  const signInWithGoogle = () => {
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');
    provider.setCustomParameters({ prompt: 'select_account' });
    return signInWithProvider(provider, 'google.com');
  };

  const signInWithMicrosoft = () => {
    const provider = new OAuthProvider('microsoft.com');
    provider.addScope('email');
    provider.addScope('profile');
    // 多租戶：任何 Microsoft 帳號皆可登入（common）
    provider.setCustomParameters({ prompt: 'select_account', tenant: 'common' });
    return signInWithProvider(provider, 'microsoft.com');
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div>載入中...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-container">
        <h1>Business Card Scanner</h1>
        <p>請先登入以使用名片掃描功能</p>
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        {pendingLink ? (
          <div className="link-account-box">
            <p>
              此電子郵件{pendingLink.email ? `（${pendingLink.email}）` : ''}先前已用
              <strong> {providerLabel(pendingLink.originalProviderId)} </strong>
              登入過。點下方按鈕用 {providerLabel(pendingLink.originalProviderId)} 登入一次，
              系統會自動把兩種登入方式連結到同一個帳號，您原本的資料不會遺失。之後兩種方式都能直接登入。
            </p>
            <button onClick={completePendingLink} className="google-signin-btn" disabled={linking}>
              用 {providerLabel(pendingLink.originalProviderId)} 登入並連結
            </button>
            {pendingLink.originalProviderId === 'google.com' && (
              <>
                <div className="link-divider">Google 帳號已無法使用？</div>
                <button onClick={migrateFromDeadGoogle} className="microsoft-signin-btn" disabled={linking}>
                  {linking ? '處理中…' : '我的 Google 已停用，改用 Microsoft 接管'}
                </button>
              </>
            )}
            <button onClick={() => setPendingLink(null)} className="link-cancel-btn" disabled={linking}>
              取消
            </button>
          </div>
        ) : (
          <>
            <button onClick={signInWithGoogle} className="google-signin-btn">
              使用 Google 登入
            </button>
            <button onClick={signInWithMicrosoft} className="microsoft-signin-btn">
              使用 Microsoft 登入
            </button>
          </>
        )}
        <div className="login-help">
          <p>如果彈出視窗被阻擋，請：</p>
          <ul>
            <li>允許此網站的彈出視窗</li>
            <li>或者點擊登入按鈕後等待頁面重定向</li>
          </ul>
        </div>
        <style jsx="true">{`
          .auth-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            padding: 40px 20px;
            text-align: center;
            position: relative;
          }

          .auth-container h1 {
            font-size: 3rem;
            margin-bottom: 16px;
            font-weight: 300;
            background: linear-gradient(135deg, #3f51b5, #673ab7);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            text-shadow: none;
          }

          .auth-container p {
            font-size: 1.1rem;
            margin-bottom: 30px;
            color: rgba(108, 99, 143, 0.8);
            font-weight: 400;
          }

          .google-signin-btn {
            background: rgba(255, 255, 255, 0.25);
            backdrop-filter: blur(20px);
            color: rgba(108, 99, 143, 0.9);
            border: 1px solid rgba(255, 255, 255, 0.4);
            padding: 16px 40px;
            font-size: 16px;
            font-weight: 600;
            border-radius: 60px;
            cursor: pointer;
            margin-top: 30px;
            box-shadow: 
              0 8px 32px rgba(31, 38, 135, 0.37),
              inset 0 1px 0 rgba(255, 255, 255, 0.5);
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            position: relative;
            overflow: hidden;
          }

          .google-signin-btn::before {
            content: '✨';
            font-size: 18px;
          }

          .google-signin-btn:hover {
            transform: translateY(-3px);
            box-shadow: 
              0 12px 40px rgba(31, 38, 135, 0.5),
              inset 0 1px 0 rgba(255, 255, 255, 0.7);
            background: rgba(255, 255, 255, 0.35);
            border-color: rgba(255, 255, 255, 0.6);
          }

          .google-signin-btn:active {
            transform: translateY(-1px);
          }

          .microsoft-signin-btn {
            background: rgba(255, 255, 255, 0.25);
            backdrop-filter: blur(20px);
            color: rgba(108, 99, 143, 0.9);
            border: 1px solid rgba(255, 255, 255, 0.4);
            padding: 16px 40px;
            font-size: 16px;
            font-weight: 600;
            border-radius: 60px;
            cursor: pointer;
            margin-top: 16px;
            box-shadow:
              0 8px 32px rgba(31, 38, 135, 0.37),
              inset 0 1px 0 rgba(255, 255, 255, 0.5);
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            position: relative;
            overflow: hidden;
          }

          .microsoft-signin-btn::before {
            content: '⊞';
            font-size: 18px;
            color: #00a4ef;
          }

          .microsoft-signin-btn:hover {
            transform: translateY(-3px);
            box-shadow:
              0 12px 40px rgba(31, 38, 135, 0.5),
              inset 0 1px 0 rgba(255, 255, 255, 0.7);
            background: rgba(255, 255, 255, 0.35);
            border-color: rgba(255, 255, 255, 0.6);
          }

          .microsoft-signin-btn:active {
            transform: translateY(-1px);
          }

          .link-account-box {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 14px;
            max-width: 420px;
            margin: 24px auto 0;
            padding: 24px;
            background: rgba(255, 255, 255, 0.3);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.45);
            border-radius: 20px;
            box-shadow:
              0 8px 32px rgba(31, 38, 135, 0.37),
              inset 0 1px 0 rgba(255, 255, 255, 0.5);
          }

          .link-account-box p {
            margin: 0 0 6px;
            font-size: 14px;
            line-height: 1.7;
            color: rgba(108, 99, 143, 0.95);
          }

          .link-account-box .google-signin-btn,
          .link-account-box .microsoft-signin-btn {
            margin-top: 0;
          }

          .link-divider {
            font-size: 12px;
            color: rgba(108, 99, 143, 0.6);
            margin-top: 4px;
          }

          .link-cancel-btn {
            background: transparent;
            border: none;
            color: rgba(108, 99, 143, 0.7);
            font-size: 13px;
            cursor: pointer;
            text-decoration: underline;
            padding: 4px 8px;
          }

          .loading-container {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            font-size: 18px;
            color: rgba(108, 99, 143, 0.8);
            font-weight: 500;
          }

          /* 移動端優化 */
          @media (max-width: 768px) {
            .auth-container h1 {
              font-size: 2rem;
            }
            
            .auth-container p {
              font-size: 1rem;
            }
            
            .google-signin-btn,
            .microsoft-signin-btn {
              padding: 12px 28px;
              font-size: 15px;
            }
          }

          .error-message {
            background: rgba(255, 107, 107, 0.2);
            backdrop-filter: blur(20px);
            color: rgba(180, 60, 60, 0.9);
            border: 1px solid rgba(255, 107, 107, 0.3);
            padding: 14px 24px;
            border-radius: 16px;
            margin: 20px 0;
            font-size: 14px;
            font-weight: 500;
            text-align: center;
            box-shadow: 0 4px 16px rgba(255, 107, 107, 0.2);
          }

          .login-help {
            margin-top: 30px;
            padding: 20px;
            background: rgba(255, 255, 255, 0.2);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.3);
            border-radius: 20px;
            box-shadow: 
              0 8px 32px rgba(31, 38, 135, 0.37),
              inset 0 1px 0 rgba(255, 255, 255, 0.5);
          }

          .login-help p {
            margin-bottom: 10px;
            font-size: 14px;
            font-weight: 500;
          }

          .login-help ul {
            list-style: none;
            padding: 0;
            margin: 0;
          }

          .login-help li {
            padding: 4px 0;
            font-size: 13px;
            opacity: 0.9;
          }

          .login-help li::before {
            content: '• ';
            color: #00d4ff;
            font-weight: bold;
            margin-right: 8px;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      <div className="user-info">
        <span>歡迎, {user.displayName || user.email}</span>
        <button onClick={handleSignOut} className="signout-btn">
          登出
        </button>
      </div>
      {children}
      <style jsx="true">{`
        .user-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          background: rgba(255, 255, 255, 0.25);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.3);
          color: rgba(108, 99, 143, 0.9);
          font-weight: 600;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5);
        }

        .signout-btn {
          background: rgba(255, 107, 107, 0.2);
          backdrop-filter: blur(20px);
          color: rgba(180, 60, 60, 0.9);
          border: 1px solid rgba(255, 107, 107, 0.3);
          padding: 10px 20px;
          font-size: 14px;
          font-weight: 600;
          border-radius: 25px;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 
            0 4px 16px rgba(255, 107, 107, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.3);
        }

        .signout-btn:hover {
          transform: translateY(-2px);
          background: rgba(255, 107, 107, 0.3);
          border-color: rgba(255, 107, 107, 0.5);
          box-shadow: 
            0 8px 25px rgba(255, 107, 107, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.5);
        }

        /* 移動端優化 */
        @media (max-width: 768px) {
          .user-info {
            padding: 10px 16px;
            font-size: 14px;
          }
          
          .signout-btn {
            padding: 6px 12px;
            font-size: 13px;
          }
        }
      `}</style>
    </div>
  );
};

export default Auth;